"use client";

import { c as _c } from "react/compiler-runtime";
import { setClientSessionTerminatedHandler } from "@dc/gateway-jsonapi";
import Cookies from "js-cookie";
import { usePathname } from "next/navigation";
import { env } from "next-runtime-env";
import { type ReactNode, useEffect, useRef } from "react";
import { AuthLoadingScreen } from "@/components/AuthLoadingScreen/AuthLoadingScreen";
import { AuthNotConfiguredState } from "@/components/AuthNotConfiguredState/AuthNotConfiguredState";
import { useSilentTidFrameCheck } from "@/components/Trimble/hooks/useSilentTidFrameCheck";
import { localeFromPath, stripLocalePrefix } from "@/lib/auth/stripLocalePrefix";
import { terminateSession } from "@/lib/authBackend/terminateSession";
import { tidClient } from "@/lib/authBackend/trimble/client";
import { trimbleAuthBackend } from "@/lib/authBackend/trimble/index";
import { reportAuthFailure } from "@/lib/authBackend/trimble/telemetry";
import { POST_LOGOUT_SESSION_KEY } from "@/lib/authBackend/trimble/tid/client";
import { TIDProvider, useAuth } from "@/lib/authBackend/trimble/tid/context";
import { buildProcoreTidLoginUrl, shouldUseProcoreTidPopup } from "@/lib/procore/tidPopup";

// Nothing reads dc.auth-backend any more; clear the 90-day leftover from browsers that set it.
const STALE_AUTH_BACKEND_COOKIE = "dc.auth-backend";

// usePathname() is locale-prefixed; keep that prefix so /login renders in the user's language
// instead of falling back to the proxy's cookie-or-default guess.
function loginPathFor(pathname: string): string {
  const locale = localeFromPath(pathname);
  return locale ? `/${locale}/login` : "/login";
}

// Blocks rendering on protected routes until Trimble auth state is known.
// On refresh, tokens are in-memory only; loginWithRedirect triggers silent
// SSO via Trimble's session cookie — typically resolves within ~500ms.
export function AuthGate(t0) {
  const $ = _c(19);
  const {
    children
  } = t0;
  const {
    isLoading,
    isAuthenticated,
    loginWithRedirect,
    client
  } = useAuth();
  const pathname = usePathname();
  const {
    startSilentCheck
  } = useSilentTidFrameCheck();
  const wasAuthenticated = useRef(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = /^(\/[a-z]{2})?\/(login|callback|logout-callback|resolve|register-public)(\/|$)/;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== pathname) {
    t2 = t1.test(pathname);
    $[1] = pathname;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isUngatedRoute = t2;
  let t3;
  let t4;
  if ($[3] !== client || $[4] !== isAuthenticated || $[5] !== isLoading || $[6] !== isUngatedRoute || $[7] !== loginWithRedirect || $[8] !== pathname || $[9] !== startSilentCheck) {
    t3 = () => {
      if (isAuthenticated) {
        wasAuthenticated.current = true;
        return;
      }
      if (wasAuthenticated.current || isUngatedRoute || isLoading) {
        return;
      }
      if (typeof window === "undefined") {
        return;
      }
      if (window.sessionStorage.getItem(POST_LOGOUT_SESSION_KEY)) {
        window.location.replace(loginPathFor(pathname));
        return;
      }
      if (shouldUseProcoreTidPopup(pathname)) {
        startSilentCheck(pathname, window.location.search, {
          onComplete: session => {
            ;
            try {
              client.adoptSession(session);
            } catch (t5) {
              const err = t5;
              reportAuthFailure("session_adopt", err, {
                pathname
              });
              window.location.replace(buildProcoreTidLoginUrl(pathname, window.location.search));
            }
          },
          onFail: () => {
            window.location.replace(buildProcoreTidLoginUrl(pathname, window.location.search));
          }
        });
        return;
      }
      const featureBranchName = env("NEXT_PUBLIC_FEATURE_BRANCH_NAME");
      const locale = localeFromPath(pathname);
      loginWithRedirect({
        redirectTo: `${stripLocalePrefix(pathname)}${window.location.search}`,
        silent: true,
        ...(locale ? {
          locale
        } : {}),
        ...(featureBranchName ? {
          featureBranch: featureBranchName
        } : {})
      });
    };
    t4 = [isLoading, isAuthenticated, isUngatedRoute, loginWithRedirect, pathname, client, startSilentCheck];
    $[3] = client;
    $[4] = isAuthenticated;
    $[5] = isLoading;
    $[6] = isUngatedRoute;
    $[7] = loginWithRedirect;
    $[8] = pathname;
    $[9] = startSilentCheck;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[12] !== client || $[13] !== pathname) {
    t5 = () => client.subscribeToRemoteLogout(() => {
      window.location.replace(shouldUseProcoreTidPopup(pathname) ? buildProcoreTidLoginUrl(pathname, window.location.search) : loginPathFor(pathname));
    });
    t6 = [client, pathname];
    $[12] = client;
    $[13] = pathname;
    $[14] = t5;
    $[15] = t6;
  } else {
    t5 = $[14];
    t6 = $[15];
  }
  useEffect(t5, t6);
  if (!isUngatedRoute && (isLoading || !isAuthenticated)) {
    let t7;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <AuthLoadingScreen />;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    return t7;
  }
  let t7;
  if ($[17] !== children) {
    t7 = <>{children}</>;
    $[17] = children;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
}
export function TrimbleAuthProvider(t0) {
  const $ = _c(6);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useEffect(_temp, t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  useEffect(_temp3, t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  useEffect(_temp4, t3);
  if (!tidClient) {
    let t4;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <AuthNotConfiguredState />;
      $[3] = t4;
    } else {
      t4 = $[3];
    }
    return t4;
  }
  let t4;
  if ($[4] !== children) {
    t4 = <TIDProvider client={tidClient}><AuthGate>{children}</AuthGate></TIDProvider>;
    $[4] = children;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
function _temp4() {
  if (tidClient) {
    return;
  }
  reportAuthFailure("not_configured", new Error("tidClient is null at the auth provider"));
}
function _temp3() {
  setClientSessionTerminatedHandler(_temp2);
}
function _temp2(t0) {
  const {
    loginUrl
  } = t0;
  terminateSession(trimbleAuthBackend, loginUrl);
}
function _temp() {
  if (Cookies.get(STALE_AUTH_BACKEND_COOKIE)) {
    Cookies.remove(STALE_AUTH_BACKEND_COOKIE, {
      path: "/"
    });
  }
}